import { buildParams } from '../../helpers/path';
import { managerApiUrl } from './manager-api';
import { baseFetch } from './utils.ts/base-fetch';

const baseUrl = `${managerApiUrl}/external-resource`;

/** Ответы вида { status, data } */
export type ManagerApiEnvelope<T> = {
  status?: boolean | number;
  data?: T;
  error?: string;
};

export function unwrapManagerData<T>(response: unknown): T | undefined {
  if (response == null) return undefined;
  if (
    typeof response === 'object' &&
    'data' in response &&
    (response as ManagerApiEnvelope<T>).data !== undefined
  ) {
    return (response as ManagerApiEnvelope<T>).data as T;
  }
  return response as T;
}

export const fetchSources = async (): Promise<{ id: number; type: string }[]> => {
  const response = await baseFetch(baseUrl + '/list');

  return response;
};

export const changeSourceStatus = async (id: string): Promise<void> => {
  await baseFetch(baseUrl + `/${id}/aggregation/enable`, {
    method: 'POST',
  });
};

export type FetchAggregationResultResponse = {
  result: string;
  coeffs: {
    positive: number;
    negative: number;
    neutral: number;
  };
};

export const fetchAggregationResults = async (
  id: string,
): Promise<FetchAggregationResultResponse> => {
  const response = await baseFetch(baseUrl + `/${id}/aggregation/results`);

  return response;
};

export type FetchAggregationDataResponse = {
  author: string;
  comment: string;
  comment_likes: number;
  post_name: string;
  post_likes: number;
}[];

export const fetchAggregationData = async (id: string): Promise<FetchAggregationDataResponse> => {
  const response = await baseFetch(baseUrl + `/${id}/aggregation/data`);

  return response;
};

// --- Дашборд «Внешний мониторинг»: aggregation/results и справочники ---

export type AggregationAspectRow = {
  aspect_id: number;
  comments_count?: number;
  negative_count?: number;
  neutral_count?: number;
  positive_count?: number;
};

export type AggregationResultsCategoryRow = {
  category_id: number;
  begin?: number;
  end?: number;
  comments_count?: number;
  aspects?: AggregationAspectRow[];
};

export type AggregationResultsLanguageRow = {
  id: number;
  comments_count?: number;
  negative_count?: number;
  neutral_count?: number;
  positive_count?: number;
  begin?: number;
  end?: number;
};

export type AggregationResultsLanguagesProviderRow = {
  provider: number;
  languages?: AggregationResultsLanguageRow[];
};

/** Строка GET .../aggregation/results?type=sentiment — бакет по времени, без категорий/аспектов */
export type AggregationResultsSentimentRow = {
  comments_count?: number;
  negative_count?: number;
  neutral_count?: number;
  positive_count?: number;
  begin?: number;
  end?: number;
};

export type ExternalResourceAspect = { id: number; name: string };
export type ExternalResourceCategory = { id: number; name: string };

export type AggregationAspectListItem = { id: number; name: string };

/** GET .../external-resource/aggregation/available */
export async function fetchAggregationAvailableResources(): Promise<{ id: number; name: string }[]> {
  const raw = await baseFetch(`${baseUrl}/aggregation/available`);
  const data = unwrapManagerData<{ id: number; name: string }[]>(raw);
  return Array.isArray(data) ? data : [];
}

/**
 * Элемент ответа GET .../external-resource/languages (`{ status, data }`).
 * Поле `name` — код с бэкенда (например `ru`, `en`, `kk`), не локализованная подпись.
 */
export type ExternalResourceLanguage = { id: number; name: string };

/** GET .../external-resource/languages → `{ status: true, data: ExternalResourceLanguage[] }` */
export async function fetchExternalResourceLanguages(): Promise<ExternalResourceLanguage[]> {
  const raw = await baseFetch(`${baseUrl}/languages`);
  const data = unwrapManagerData<unknown>(raw);
  if (!Array.isArray(data)) return [];
  return data.filter(
    (x): x is ExternalResourceLanguage =>
      x != null &&
      typeof x === 'object' &&
      typeof (x as ExternalResourceLanguage).id === 'number' &&
      Number.isFinite((x as ExternalResourceLanguage).id) &&
      typeof (x as ExternalResourceLanguage).name === 'string',
  );
}

/**
 * GET .../external-resource/aggregation/results?resources=&begin=&end=&grouping_interval=&type=
 * `resources` — один id источника или несколько id **через запятую** («все источники»).
 * `type` — разрез агрегации: `categories` | `languages`.
 */
export async function fetchAggregationResultsRows(
  resources: string,
  params?: {
    begin?: number;
    end?: number;
    grouping_interval?: 'day' | 'week' | 'month';
    type?: 'categories' | 'languages' | 'sentiment';
  },
): Promise<AggregationResultsCategoryRow[]> {
  const url = `${baseUrl}/aggregation/results${buildParams({
    resources,
    ...(params ?? {}),
  })}`;
  const raw = await baseFetch(url);
  const data = unwrapManagerData<AggregationResultsCategoryRow[]>(raw);
  return Array.isArray(data) ? data : [];
}

/** GET .../external-resource/aggregation/results?type=languages */
export async function fetchAggregationResultsLanguagesRows(
  resources: string,
  params?: {
    begin?: number;
    end?: number;
    grouping_interval?: 'day' | 'week' | 'month';
  },
): Promise<AggregationResultsLanguagesProviderRow[]> {
  const url = `${baseUrl}/aggregation/results${buildParams({
    resources,
    ...(params ?? {}),
    type: 'languages',
  })}`;
  const raw = await baseFetch(url);
  const data = unwrapManagerData<AggregationResultsLanguagesProviderRow[]>(raw);
  return Array.isArray(data) ? data : [];
}

/** GET .../external-resource/aggregation/results?type=sentiment */
export async function fetchAggregationResultsSentimentRows(
  resources: string,
  params?: {
    begin?: number;
    end?: number;
    grouping_interval?: 'day' | 'week' | 'month';
  },
): Promise<AggregationResultsSentimentRow[]> {
  const url = `${baseUrl}/aggregation/results${buildParams({
    resources,
    ...(params ?? {}),
    type: 'sentiment',
  })}`;
  const raw = await baseFetch(url);
  const data = unwrapManagerData<unknown>(raw);
  if (!Array.isArray(data)) return [];
  return data.filter(
    (x): x is AggregationResultsSentimentRow =>
      x != null &&
      typeof x === 'object' &&
      ('begin' in x || 'comments_count' in x || 'positive_count' in x),
  );
}

/** GET .../external-resource/aspects */
export async function fetchAggregationAspectsList(): Promise<AggregationAspectListItem[]> {
  const raw = await baseFetch(`${baseUrl}/aspects`);
  const data = unwrapManagerData<AggregationAspectListItem[]>(raw);
  return Array.isArray(data) ? data : [];
}

/** GET .../external-resource/categories */
export async function fetchExternalResourceCategories(): Promise<ExternalResourceCategory[]> {
  const raw = await baseFetch(`${baseUrl}/categories`);
  const data = unwrapManagerData<ExternalResourceCategory[]>(raw);
  return Array.isArray(data) ? data : [];
}
